import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { FaPhoneAlt, FaCommentDots, FaUserCircle } from "react-icons/fa";

const Analytics = () => {
  const barData = [
    { name: "Pipeline", value: 180 },
    { name: "Follow Up", value: 120 },
    { name: "Schedule", value: 220 },
    { name: "Conversation", value: 100 },
  ];

  const leadSourceData = [
    { name: "Google", value: 40, color: "#28a745" },
    { name: "Paid", value: 35, color: "#ffc107" },
    { name: "Campaigns", value: 15, color: "#dc3545" },
    { name: "Referrals", value: 10, color: "#6f42c1" },
  ];

  const contacts = Array(4).fill({
    name: "Antony",
    email: "antony@example.com",
    phone: "1234567890",
    date: "14 Jan 2024",
  });

  const recentDeals = Array(4).fill({
    name: "Collins",
    stage: "Qualify to Buy",
    value: "$4,50,000",
    owner: "Antony",
    date: "14 Jan 2024",
  });

  const recentLeads = Array(4).fill({
    name: "Collins",
    company: "Bigwave Solutions",
    stage: "Qualified",
    date: "14 Jan 2024",
    owner: "Antony",
  });

  const recentCompanies = Array(4).fill({
    company: "Bigwave Solutions",
    email: "bigwave@example.com",
    phone: "1234567890",
    owner: "Antony",
    date: "14 Jan 2025",
  });

  const activities = [
    {
      icon: <FaPhoneAlt />,
      color: "success",
      title: "Drain responded to your appointment schedule question.",
      time: "09:25 PM",
    },
    {
      icon: <FaCommentDots />,
      color: "primary",
      title: "You sent 1 Message to James.",
      time: "09:25 PM",
    },
    {
      icon: <FaPhoneAlt />,
      color: "success",
      title: "Denwar responded to your appointment.",
      time: "09:25 PM",
    },
    {
      icon: <FaUserCircle />,
      color: "dark",
      title: "Meeting with Antony.",
      time: "09:25 PM",
    },
  ];

  return (
    <div className="container-fluid p-4 bg-light">
      <h3 className="fw-bold mb-4">Analytics</h3>

      {/* TOP ROW */}
      <div className="row g-4">
        {/* Deals by Stage */}
        <div className="col-lg-6 d-flex">
          <div className="card shadow-sm rounded-4 w-100 h-100">
            {/* Header */}
            <div className="card-header d-flex justify-content-between bg-white">
              <span className="fw-semibold">Deals by Stage</span>
              <span className="badge bg-light text-danger border">
                This Week
              </span>
            </div>

            {/* Body */}
            <div className="card-body">
              {/* Top text */}
              <p className="mb-3 fw-semibold">
                98% <span className="text-success">▲ vs last year</span>
              </p>

              {/* Chart */}
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={barData}
                  margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                >
                  <CartesianGrid stroke="#eee" vertical={false} />

                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    cursor={{ fill: "rgba(0,0,0,0.05)" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />

                  <Bar
                    dataKey="value"
                    fill="#1e1245"
                    radius={[6, 6, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Leads by Source */}
        <div className="col-lg-6 d-flex">
          <div className="card shadow-sm rounded-4 w-100 h-100">
            {/* Header */}
            <div className="card-header d-flex justify-content-between align-items-center bg-white border-0">
              <span className="fw-semibold">Leads by Source</span>
              <span className="badge bg-danger">This Week</span>
            </div>

            {/* Body */}
            <div className="card-body text-center p-2">
              {/* Pie Chart */}
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={leadSourceData}
                    dataKey="value"
                    innerRadius={40}
                    outerRadius={65}
                    paddingAngle={2}
                  >
                    {leadSourceData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Legend */}
              <div className="mt-2 text-start small text-muted px-2">
                {leadSourceData.map((item, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-1">
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: item.color,
                        display: "inline-block",
                      }}
                    ></span>
                    <span>
                      {item.name} {item.value}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECOND ROW */}
      {/* Won Deals */}
      <div className="row g-4 mt-2">
        <div className="col-lg-6 d-flex">
          <div className="card shadow-sm rounded-4 w-100 h-100 text-center">
            <div className="card-header d-flex justify-content-between bg-white">
              <span className="fw-semibold">Won Deals Stage</span>
              <span className="badge bg-light text-dark">Sales Pipeline</span>
            </div>

            <div className="card-body">
              <p className="mb-1 text-muted">Stages Won This Year</p>

              <h4 className="fw-bold">$45,899.79</h4>
              <span className="badge bg-danger-subtle text-danger px-3 py-2 mb-4">
                $45,699.79
              </span>

              {/* Bubble Container */}
              <div
                className="position-relative mx-auto"
                style={{ width: 260, height: 200 }}
              >
                {/* Conversation (Left Big) */}
                <div
                  className="position-absolute d-flex flex-column justify-content-center align-items-center text-white"
                  style={{
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    background: "#ff8c42",
                    left: 0,
                    top: 40,
                    fontSize: 13,
                  }}
                >
                  <div>Conversation</div>
                  <small>48%</small>
                </div>

                {/* Email (Right Big) */}
                <div
                  className="position-absolute d-flex flex-column justify-content-center align-items-center text-white"
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: "#f4c430",
                    right: 0,
                    top: 50,
                    fontSize: 13,
                  }}
                >
                  <div>Email</div>
                  <small>38%</small>
                </div>

                {/* Calls (Top) */}
                <div
                  className="position-absolute d-flex flex-column justify-content-center align-items-center text-white"
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "#e74c3c",
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: 0,
                    fontSize: 12,
                  }}
                >
                  <div>Calls</div>
                  <small>48%</small>
                </div>

                {/* Chats (Bottom) */}
                <div
                  className="position-absolute d-flex flex-column justify-content-center align-items-center text-white"
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "#28a745",
                    left: "50%",
                    transform: "translateX(-50%)",
                    bottom: 0,
                    fontSize: 12,
                  }}
                >
                  <div>Chats</div>
                  <small>20%</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activities */}
        <div className="col-lg-6 d-flex">
          <div className="card shadow-sm rounded-4 w-100 h-100">
            <div className="card-header bg-white fw-semibold">
              Recent Activities
            </div>

            <div className="card-body">
              <div className="position-relative">
                {/* vertical line */}
                <div
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "1px",
                    bottom: 8,
                    width: "4px",
                    background: "#181a1bda",
                  }}
                ></div>

                {activities.map((a, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-start mb-4 position-relative"
                  >
                    {/* icon circle */}
                    <div
                      className={`bg-${a.color} text-white rounded-circle d-flex justify-content-center align-items-center`}
                      style={{
                        width: 38,
                        height: 38,
                        zIndex: 1,
                      }}
                    >
                      {a.icon}
                    </div>

                    {/* content */}
                    <div className="ms-3">
                      <div className="fw-medium" style={{ fontSize: "14px" }}>
                        {a.title}
                      </div>
                      <small className="text-muted">{a.time}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONTACTS */}
      <div className="row g-4 mt-4">
        <div className="col-12">
          <div className="card shadow-sm rounded-4 h-100">
            <div className="card-header d-flex justify-content-between bg-white">
              <span className="fw-semibold">Recently Created Contacts</span>
              <span className="badge bg-danger">This Week</span>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Contacts</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {contacts.map((c, i) => (
                      <tr key={i}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <FaUserCircle size={20} />
                            <div>
                              <div className="fw-semibold">{c.name}</div>
                              <small className="text-muted">Manager</small>
                            </div>
                          </div>
                        </td>
                        <td>{c.email}</td>
                        <td>{c.phone}</td>
                        <td>{c.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DEALS */}
      <div className="row g-4 mt-4">
        <div className="col-12">
          <div className="card shadow-sm rounded-4 h-100">
            <div className="card-header d-flex justify-content-between bg-white">
              <span className="fw-semibold">Recent Deals</span>
              <button className="btn btn-sm btn-outline-primary">
                View All
              </button>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Stage</th>
                      <th>Value</th>
                      <th>Owner</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recentDeals.map((d, i) => (
                      <tr key={i}>
                        <td>{d.name}</td>
                        <td>{d.stage}</td>
                        <td className="fw-semibold">{d.value}</td>
                        <td>{d.owner}</td>
                        <td>{d.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LEADS + COMPANIES */}
      <div className="row g-4 mt-4">
        <div className="col-12">
          <div className="card shadow-sm rounded-4 h-100">
            <div className="card-header bg-white fw-semibold">Recent Leads</div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Stage</th>
                      <th>Date</th>
                      <th>Owner</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recentLeads.map((l, i) => (
                      <tr key={i}>
                        <td>{l.name}</td>
                        <td>{l.company}</td>
                        <td>
                          <span className="badge bg-info text-dark">
                            {l.stage}
                          </span>
                        </td>
                        <td>{l.date}</td>
                        <td>{l.owner}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm rounded-4 h-100">
            <div className="card-header bg-white fw-semibold">
              Recent Companies
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Owner</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {recentCompanies.map((c, i) => (
                      <tr key={i}>
                        <td className="fw-semibold">{c.company}</td>
                        <td>{c.email}</td>
                        <td>{c.phone}</td>
                        <td>{c.owner}</td>
                        <td>{c.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Analytics;
